import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  getMe,
  updateProfileName,
  updatePreferredDisplayBoard,
  uploadAvatar,
  resolveAvatarUrl,
  queryKeys,
} from "@/lib/api";
import { useBoards } from "@/hooks/use-api";
import { useAuthStore } from "@/stores/auth.store";
import { UserAvatar } from "@/components/ui/user-avatar";

const NO_AUTO_DISPLAY_VALUE = "__none__";

export function ProfilePage() {
  const { user, setUser } = useAuthStore();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(user?.name ?? "");
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarCacheBust, setAvatarCacheBust] = useState(0);
  const [preferredBoardValue, setPreferredBoardValue] = useState(NO_AUTO_DISPLAY_VALUE);
  const { t } = useTranslation();

  const { data: boards = [] } = useBoards();

  const { data: profile } = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const res = await getMe();
      if (!res.ok) throw new Error(res.error);
      return res.data.user;
    },
    staleTime: 60_000,
  });

  const currentUser = profile ?? user;
  const avatarSrc = currentUser ? resolveAvatarUrl(currentUser) || null : null;
  const currentPreferredBoardId = currentUser?.preferred_display_board ?? "";
  const selectedPreferredBoardId =
    preferredBoardValue === NO_AUTO_DISPLAY_VALUE ? "" : preferredBoardValue;
  const isPreferredDirty = selectedPreferredBoardId !== currentPreferredBoardId;

  const [showAvatar, setShowAvatar] = useState(true);

  useEffect(() => {
    setPreferredBoardValue(currentPreferredBoardId || NO_AUTO_DISPLAY_VALUE);
  }, [currentPreferredBoardId]);

  const nameMutation = useMutation({
    mutationFn: (newName: string) => updateProfileName(newName),
    onSuccess: (res) => {
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      if (user) setUser({ ...user, name: res.data.user.name });
      qc.invalidateQueries({ queryKey: queryKeys.me });
      toast.success(t("profile.save_name_success"));
    },
    onError: () => toast.error(t("profile.save_name_error")),
  });

  const preferredBoardMutation = useMutation({
    mutationFn: (boardId: string | null) => updatePreferredDisplayBoard(boardId),
    onSuccess: (res) => {
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      if (user) {
        setUser({
          ...user,
          preferred_display_board: res.data.preferred_display_board,
        });
      }
      qc.invalidateQueries({ queryKey: queryKeys.me });
      toast.success(t("profile.auto_display_saved"));
    },
    onError: () => toast.error(t("profile.auto_display_save_error")),
  });

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarLoading(true);
    try {
      const res = await uploadAvatar(file);
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      // Mise à jour du store pour forcer le re-render de la nav
      // (nouveau filename + cache-bust).
      if (user) {
        setUser({
          ...user,
          avatar: res.data.user.avatar || undefined,
          avatar_version: Date.now(),
        });
      }
      setShowAvatar(false);
      setAvatarCacheBust((n) => n + 1);
      setTimeout(() => setShowAvatar(true), 50);
      qc.invalidateQueries({ queryKey: queryKeys.me });
      toast.success(t("profile.avatar_success"));
    } finally {
      setAvatarLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function handleNameSave() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === currentUser?.name) return;
    nameMutation.mutate(trimmed);
  }

  function handlePreferredBoardSave() {
    const nextValue = selectedPreferredBoardId || null;
    preferredBoardMutation.mutate(nextValue);
  }

  const initials = (currentUser?.name ?? "?");

  return (
    <Box p={{ initial: "4", sm: "6" }} style={{ maxWidth: 480 }}>
      <Heading size="5" mb="6">
        {t("profile.title")}
      </Heading>

      {/* Avatar */}
      <Flex direction="column" align="center" gap="3" mb="6">
        <Box
          style={{
            position: "relative",
            cursor: avatarLoading ? "wait" : "pointer",
            opacity: avatarLoading ? 0.6 : 1,
            transition: "opacity 0.2s",
          }}
          onClick={() => !avatarLoading && fileRef.current?.click()}
        >
          {showAvatar ? (
            <UserAvatar
              size="6"
              src={avatarSrc}
              name={initials}
              version={avatarCacheBust || null}
              radius="full"
              style={{ width: 80, height: 80 }}
            />
          ) : (
            <UserAvatar
              size="6"
              name={initials}
              radius="full"
              style={{ width: 80, height: 80 }}
            />
          )}
        </Box>
        <Button
          variant="ghost"
          size="2"
          style={{ minHeight: 44 }}
          onClick={() => fileRef.current?.click()}
          disabled={avatarLoading}
        >
          {avatarLoading ? t("profile.uploading_avatar") : t("profile.change_avatar_btn")}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={handleAvatarChange}
        />
      </Flex>

      <Separator size="4" mb="5" />

      {/* Nom */}
      <Flex direction="column" gap="2" mb="5">
        <Text size="2" weight="medium">
          {t("profile.name_display_label")}
        </Text>
        <Flex gap="2">
          <TextField.Root
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleNameSave();
            }}
            disabled={nameMutation.isPending}
            style={{ flexGrow: 1, minHeight: 44 }}
          />
          <Button
            onClick={handleNameSave}
            disabled={
              nameMutation.isPending ||
              !name.trim() ||
              name.trim() === currentUser?.name
            }
            style={{ minHeight: 44 }}
          >
            {nameMutation.isPending ? "…" : t("common.save")}
          </Button>
        </Flex>
      </Flex>

      {/* Email (lecture seule) */}
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          {t("profile.email_label")}
        </Text>
        <TextField.Root
          value={currentUser?.email ?? ""}
          readOnly
          disabled
          style={{ minHeight: 44 }}
        />
      </Flex>

      <Separator size="4" my="5" />

      {/* Préférence display auto au login */}
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          {t("profile.auto_display_label")}
        </Text>
        <Text size="1" color="gray">
          {t("profile.auto_display_help")}
        </Text>
        <Flex gap="2" align={{ initial: "stretch", sm: "end" }} direction={{ initial: "column", sm: "row" }}>
          <Box style={{ flexGrow: 1 }}>
            <Select.Root
              value={preferredBoardValue}
              onValueChange={setPreferredBoardValue}
              disabled={preferredBoardMutation.isPending}
            >
              <Select.Trigger style={{ minHeight: 44 }} />
              <Select.Content>
                <Select.Item value={NO_AUTO_DISPLAY_VALUE}>
                  {t("profile.auto_display_none")}
                </Select.Item>
                {boards.map((board) => (
                  <Select.Item key={board.id} value={board.id}>
                    {board.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
          <Button
            onClick={handlePreferredBoardSave}
            disabled={preferredBoardMutation.isPending || !isPreferredDirty}
            style={{ minHeight: 44 }}
          >
            {preferredBoardMutation.isPending ? "…" : t("common.save")}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
